import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { PublicProfile } from '@/types/profiles';
import { getAvatarUrl } from '@/lib/utils/avatars';

interface UserInfoCardProps {
  profile: PublicProfile;
  isOwnProfile: boolean;
  userOffers?: any[];
}

export const UserInfoCard = ({ profile, isOwnProfile, userOffers }: UserInfoCardProps) => {
  const memberSince = new Date(profile.created_at);
  const formattedJoinDate = memberSince.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get the avatar URL, falling back to UI Avatars if none is provided
  const avatarUrl = getAvatarUrl(profile.avatar_url, profile.name || 'User');

  return (
    <Card className="md:col-span-1">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>User Profile</span>
          {isOwnProfile && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/profile">Edit</Link>
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center text-center">
          <Avatar className="w-24 h-24 mb-4">
            <AvatarImage src={avatarUrl} alt={profile.name || "User"} />
            <AvatarFallback>{(profile.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <h2 className="text-xl font-bold mb-1">{profile.name || "Unnamed User"}</h2>
          <div className="flex items-center text-sm text-muted-foreground mb-4">
            <Calendar className="h-4 w-4 mr-1" />
            <span>Member since {formattedJoinDate}</span>
          </div>
          
          <div className="w-full grid grid-cols-2 gap-2 mb-6">
            <div className="bg-muted rounded-lg p-3 text-center">
              <div className="text-lg font-bold">{userOffers?.length || 0}</div>
              <div className="text-xs text-muted-foreground">Active Offers</div>
            </div>
            <div className="bg-muted rounded-lg p-3 text-center">
              <div className="text-lg font-bold">4.8</div>
              <div className="text-xs text-muted-foreground">Avg. Rating</div>
            </div>
          </div>
          
          <div className="w-full text-left mb-4">
            <h3 className="font-medium mb-2">Bio</h3>
            <p className="text-sm text-muted-foreground">
              {profile.bio || "This user hasn't added a bio yet."}
            </p>
          </div>
          
          {!isOwnProfile && (
            <Button className="w-full mt-2">
              Contact
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
